/**
 * Sidebar — primary navigation.
 *
 * Spec: Docs/design/04-shell-and-navigation.md §3
 *
 * 240px expanded (lg+), 56px collapsed (md), off-canvas (xs/sm).
 * Groups: Workspace, Build, Connect, Operate.
 */
import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PanelLeft, PanelLeftClose, MessageSquare } from "lucide-react";
import { cn } from "@/lib/cn";
import { BrandLockup } from "@/components/landing/BrandLockup";
import { SimpleTooltip } from "@/components/ui/Tooltip";
import { DysonLogo } from "@/components/ui/DysonLogo";
import { useAuth } from "@/auth/useAuth";
import { useSessionPanel } from "@/contexts/SessionPanelContext";
import { HOME_PATH, isHomePath } from "@/lib/routes";

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: string | number;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: "Workspace",
    items: [
      { label: "Home", href: HOME_PATH, icon: <IconHome /> },
      { label: "Projects", href: "/projects", icon: <IconProjects /> },
      { label: "Inbox", href: "/inbox", icon: <IconInbox /> },
      { label: "Runs", href: "/runs", icon: <IconRuns /> },
    ],
  },
  {
    title: "Build",
    items: [
      { label: "Agents", href: "/agents", icon: <IconAgents /> },
      { label: "Orchestration", href: "/orchestration", icon: <IconAgents /> },
      { label: "Tasks", href: "/tasks", icon: <IconTasks /> },
      { label: "Workflows", href: "/workflows", icon: <IconScheduler /> },
      { label: "Automations", href: "/automations", icon: <IconScheduler /> },
      { label: "RIDE", href: "/ride", icon: <IconRide /> },
      { label: "Skills", href: "/skills", icon: <IconSkills /> },
      { label: "Memory", href: "/memory", icon: <IconMemory /> },
    ],
  },
  {
    title: "Connect",
    items: [
      { label: "Devices", href: "/devices", icon: <IconDevices /> },
      { label: "MCP Hub", href: "/mcp-hub", icon: <IconMcp /> },
    ],
  },
  {
    title: "Operate",
    items: [
      { label: "Files", href: "/files", icon: <IconFiles /> },
      { label: "Storage", href: "/storage", icon: <IconStorage /> },
      { label: "Permissions", href: "/permissions", icon: <IconPermissions /> },
      { label: "Settings", href: "/settings", icon: <IconSettings /> },
    ],
  },
];

const ADMIN_NAV_GROUP: NavGroup = {
  title: "Admin",
  items: [
    { label: "Settings", href: "/admin/settings", icon: <IconSettings /> },
    { label: "Health", href: "/admin/health", icon: <IconObservability /> },
    // Org-wide costs, SLOs and audit: every source is an admin endpoint.
    { label: "Observability", href: "/observability", icon: <IconObservability /> },
  ],
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { open: sessionPanelOpen, setOpen: setSessionPanelOpen } = useSessionPanel();
  const navGroups = React.useMemo(
    () => (auth.isAdmin ? [...NAV_GROUPS, ADMIN_NAV_GROUP] : NAV_GROUPS),
    [auth.isAdmin],
  );

  const isActive = (href: string) => {
    if (href === HOME_PATH) return isHomePath(location.pathname);
    return location.pathname.startsWith(href);
  };

  const chatsActive = sessionPanelOpen && isHomePath(location.pathname);

  const toggleChatsPanel = React.useCallback(() => {
    onMobileClose();
    if (isHomePath(location.pathname) && sessionPanelOpen) {
      setSessionPanelOpen(false);
      return;
    }
    if (!isHomePath(location.pathname)) {
      navigate(HOME_PATH);
    }
    setSessionPanelOpen(true);
  }, [location.pathname, navigate, onMobileClose, sessionPanelOpen, setSessionPanelOpen]);

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Logo area */}
      <div className={cn("flex h-[44px] items-center border-b border-border px-4", collapsed && "justify-center px-0")}>
        {!collapsed && (
          <Link to={HOME_PATH} className="text-fg">
            <BrandLockup variant="inline" size="sm" />
          </Link>
        )}
        {collapsed && (
          <Link to={HOME_PATH}>
            <SimpleTooltip label="Omni" side="bottom">
              <DysonLogo className="h-5 w-5" />
            </SimpleTooltip>
          </Link>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto py-2" aria-label="main navigation">
        {navGroups.map((group) => (
          <div key={group.title} className="mb-1">
            {!collapsed && (
              <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-fg-faint">
                {group.title}
              </div>
            )}
            {collapsed && <div className="my-1 mx-3 border-t border-border" />}
            <ul>
              {group.items.map((item) => {
                const active = isActive(item.href);
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      onClick={onMobileClose}
                      title={collapsed ? item.label : undefined}
                      className={cn(
                        "flex items-center gap-3 px-4 py-1.5 text-xs transition-colors",
                        collapsed && "justify-center px-0 py-2",
                        active
                          ? "text-accent bg-accent-bg"
                          : "text-fg-muted hover:text-fg hover:bg-bg-sunken",
                      )}
                    >
                      <span className="flex-shrink-0 w-4 h-4">{item.icon}</span>
                      {!collapsed && (
                        <>
                          <span className="flex-1">{item.label}</span>
                          {item.badge != null && (
                            <span className="text-[10px] font-mono text-fg-faint bg-bg-sunken px-1.5 py-0.5">
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  </li>
                );
              })}
              {group.title === "Workspace" && (
                <li>
                  <button
                    type="button"
                    onClick={toggleChatsPanel}
                    title={collapsed ? "Chats" : undefined}
                    aria-pressed={chatsActive}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-1.5 text-xs transition-colors",
                      collapsed && "justify-center px-0 py-2",
                      chatsActive
                        ? "text-accent bg-accent-bg"
                        : "text-fg-muted hover:text-fg hover:bg-bg-sunken",
                    )}
                  >
                    <span className="flex-shrink-0 w-4 h-4">
                      <MessageSquare size={16} strokeWidth={1.5} />
                    </span>
                    {!collapsed && <span className="flex-1 text-left">Chats</span>}
                  </button>
                </li>
              )}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer collapse toggle (desktop only) */}
      <button
        type="button"
        onClick={onToggle}
        className="hidden md:flex items-center justify-center gap-2 h-9 border-t border-border text-fg-faint hover:text-fg transition-colors"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={collapsed ? "Expand sidebar (Ctrl+B)" : "Collapse sidebar (Ctrl+B)"}
      >
        {collapsed ? (
          <PanelLeft size={16} strokeWidth={1.5} aria-hidden />
        ) : (
          <>
            <PanelLeftClose size={14} strokeWidth={1.5} aria-hidden />
            <span className="text-[10px] font-mono">Collapse</span>
          </>
        )}
      </button>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar — relative so the edge-strip toggle can anchor
          to its right edge without re-layouting the main content. */}
      <aside
        className={cn(
          "relative hidden md:flex flex-col border-r border-border bg-bg-elevated transition-all duration-200",
          collapsed ? "w-14" : "w-60",
        )}
      >
        {sidebarContent}

        {/* Edge-strip toggle — a slim hit-zone on the right edge that
            reveals a centred icon button on hover. Always visible at
            the midpoint via the button itself; the strip widens the
            click target so the user doesn't need pixel precision. */}
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar (Ctrl+B)" : "Collapse sidebar (Ctrl+B)"}
          className={cn(
            "group absolute top-1/2 -translate-y-1/2",
            // Anchor: half-overlap the border so the icon sits ON the divider.
            "-right-3 z-30",
            // 24px wide hit area centred on a 16px icon chip.
            "flex h-7 w-6 items-center justify-center",
            // Visual chip: bg-elevated panel with border to look like a tab.
            "bg-bg-elevated border border-border",
            "text-fg-faint hover:text-fg hover:border-border-strong",
            "transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
          )}
        >
          {collapsed ? (
            <PanelLeft size={13} strokeWidth={1.5} aria-hidden />
          ) : (
            <PanelLeftClose size={13} strokeWidth={1.5} aria-hidden />
          )}
        </button>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-[var(--om-color-overlay)] md:hidden"
            onClick={onMobileClose}
            aria-hidden
          />
          <aside className="fixed inset-y-0 left-0 z-50 w-60 bg-bg-elevated border-r border-border md:hidden">
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
}

/* ─── Icon Components (stroke-only, 1.5px, per brand rules) ─── */

function IconHome() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>);
}
function IconInbox() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>);
}
function IconRuns() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>);
}
function IconAgents() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>);
}
function IconScheduler() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>);
}
function IconTasks() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>);
}
function IconRide() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>);
}
function IconSkills() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>);
}
function IconProjects() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h6l2 2h10v10H3z" /><line x1="3" y1="7" x2="3" y2="5" /><line x1="3" y1="5" x2="9" y2="5" /></svg>);
}
function IconMemory() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>);
}
function IconDevices() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>);
}
function IconMcp() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" /><rect x="2" y="14" width="20" height="8" rx="2" /><line x1="6" y1="6" x2="6.01" y2="6" /><line x1="6" y1="18" x2="6.01" y2="18" /></svg>);
}
function IconFiles() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>);
}
function IconPermissions() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>);
}
function IconObservability() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>);
}
function IconSettings() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>);
}
function IconStorage() {
  return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>);
}
